import { memo, useEffect, useRef, useState } from 'react'

import { Button, Text } from '@cash-me/react-components'
import NextImage from 'next/image'

import { Polygon } from 'components/polygon'

import { homeRenovationUseCases } from './data'
import * as S from './styles'

type Props = {
  onClick: () => void
}

export const HomeRenovationWaysToUse = memo(({ onClick }: Props) => {
  const sectionRef = useRef<HTMLElement>(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const section = sectionRef.current

    if (!section || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true)
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          return
        }

        if (entry.boundingClientRect.top > 0) setIsVisible(false)
      },
      { threshold: 0.3 }
    )

    observer.observe(section)

    return () => observer.disconnect()
  }, [])

  return (
    <S.Section ref={sectionRef} aria-labelledby="home-renovation-ways-to-use-title">
      <S.Layout>
        <S.Content>
          <S.Introduction>
            <Text
              id="home-renovation-ways-to-use-title"
              as="h2"
              size="heading2"
              weight="medium"
              color="var(--colors-neutral-text-contrast-default)"
            >
              Como posso usar o Empréstimo para <S.Highlight>Reforma de Casa?</S.Highlight>
            </Text>

            <Text size="bodySmall" color="var(--colors-neutral-text-contrast-default)">
              Ele pode ser usado para diferentes objetivos, como:
            </Text>
          </S.Introduction>

          <S.UseCaseList>
            {homeRenovationUseCases.map(({ id, icon, title, description }) => (
              <S.UseCase key={id}>
                <S.Icon aria-hidden>{icon}</S.Icon>
                <S.UseCaseCopy>
                  <Text as="h3" size="bodySmall" weight="bold" color="var(--colors-neutral-text-contrast-default)">
                    {title}
                  </Text>
                  <Text size="bodySmall" color="var(--colors-neutral-text-contrast-default)">
                    {description}
                  </Text>
                </S.UseCaseCopy>
              </S.UseCase>
            ))}
          </S.UseCaseList>

          <Button
            aria-label="Simular crédito para reforma de casa agora"
            size="lg"
            type="button"
            variants="text-only"
            onClick={onClick}
          >
            Simular agora
          </Button>
        </S.Content>

        <S.MediaPanel>
          <S.ImageWrapper className={isVisible ? 'animate' : ''}>
            <NextImage
              src={`${process.env.NEXT_PUBLIC_SITE_IMAGES_PREFIX}/images/site/home-renovation-loan/ways-to-use.png`}
              alt="Pessoas reunidas em uma sala reformada"
              fill
              quality={100}
              sizes="(min-width: 1240px) 45vw, 0px"
            />
          </S.ImageWrapper>

          <S.Callout>
            <S.CalloutPolygon aria-hidden>
              <Polygon type="TriangleRound" draw="fill" color="#fa0890" width={33} height={40} />
            </S.CalloutPolygon>

            <Text as="h3" size="heading5" weight="medium" color="var(--colors-neutral-text-default)">
              Sonha com ambientes mais <strong>bonitos e funcionais?</strong>
            </Text>

            <Text size="bodySmall" weight="bold" color="var(--colors-blue-text-default)">
              Invista em decoração, móveis planejados e projetos de interiores.
            </Text>

            <Text size="bodySmall" color="var(--colors-neutral-text-default)">
              Na CashMe, <strong>o crédito não fica preso a uma única finalidade.</strong> Você pode utilizar parte do
              valor na reforma e direcionar outra parte para diferentes objetivos pessoais ou profissionais, como ter{' '}
              <strong>capital de giro, fazer um investimento, pagar por um curso no exterior, e muito mais.</strong>
            </Text>

            <S.MobileCalloutButton>
              <Button
                aria-label="Simular crédito para reforma de casa pelo painel de ambientes"
                size="lg"
                type="button"
                variants="text-only"
                onClick={onClick}
              >
                Simular agora
              </Button>
            </S.MobileCalloutButton>
          </S.Callout>
        </S.MediaPanel>
      </S.Layout>
    </S.Section>
  )
})

HomeRenovationWaysToUse.displayName = 'HomeRenovationWaysToUse'
